import api from 'api'
import { AxiosError, AxiosPromise, AxiosResponse } from 'axios'
import { TipoReceitaEnum } from 'graphql/types.generated'
import { GerarAtendimentoDigitalInput } from 'view/assinatura-eletronica-atend/model-assinaturaEletronicaAtend'
import { AtestadoRecenteImpressaoInput } from 'view/atendimentos/detail/soap/plano/atestados/model-atestado'
import { ImpressaoMedicamentosInput } from 'view/atendimentos/detail/soap/plano/prescricao-medicamento/modal/impressao/ImpressaoMedicamentosInput'

import { ContaGovBrConfiabilidadeInsuficienteMessage } from './ContaGovBrConfiabilidadeInsuficienteMessage'

export enum TipoDocumentoAssinaturaDigitalPopup {
  PRESCRICAO,
  ATESTADO,
  BUNDLE_FHIR,
  ATENDIMENTO,
}

export enum StatusAssinaturaDigitalPopup {
  SUCCESS = 'success',
  FAILURE = 'failure',
  CANCELED = 'canceled',
}

export const INTERVAL_DELAY_ASSINATURA_DIGITAL_POPUP = 500 // ms

export type GerarAssinaturaDigitalInputType =
  | ImpressaoMedicamentosInput
  | AtestadoRecenteImpressaoInput
  | EmitirBundleFhirDigitalPopupInput
  | GerarAtendimentoDigitalInput

export type EmitirAssinaturaDigitalInputType =
  | EmitirPrescricaoDigitalInput
  | EmitirAssinaturaDigitalInput
  | EmitirBundleFhirDigitalInput

export interface AssinaturaDigitalPopupResult<T> {
  data: AssinaturaDigitalPopupResponse
  openPopup: (input: T) => Promise<void>
  closePopup: () => void
}

export interface AssinaturaDigitalPopupResponse {
  status?: StatusAssinaturaDigitalPopup
  response?: AxiosResponse
  codigoDocumento?: string
  error?: DocumentoDigitalErrorCodeEnum
  requestError?: AxiosError<string>
  identifier: string
}

export interface EmitirAssinaturaDigitalPopupInput {
  atendimentoId: ID
  cidadaoId?: ID
  emailCidadao?: string
}

export interface EmitirBundleFhirDigitalPopupInput {
  envioRndsId: ID
  atendimentoId: ID
}

export interface EmitirPrescricaoDigitalInput extends EmitirAssinaturaDigitalPopupInput {
  codigoDocumento: string
  tipoReceita: TipoReceitaEnum
}

export interface EmitirAssinaturaDigitalInput extends EmitirAssinaturaDigitalPopupInput {
  codigoDocumento: string
}

export interface EmitirBundleFhirDigitalInput extends EmitirBundleFhirDigitalPopupInput {
  codigoDocumento: string
}

export interface EmitirPrescricaoDigitalPopupInput extends EmitirAssinaturaDigitalPopupInput {
  tipoReceita: TipoReceitaEnum
}

interface AssinaturaDigitalPopupConfig {
  target: string
  gerar: (input: GerarAssinaturaDigitalInputType) => AxiosPromise
  emitir: (input: EmitirAssinaturaDigitalInputType) => AxiosPromise
}

export const configAssinaturaDigitalByTipoDocumento: Record<
  TipoDocumentoAssinaturaDigitalPopup,
  AssinaturaDigitalPopupConfig
> = {
  [TipoDocumentoAssinaturaDigitalPopup.PRESCRICAO]: {
    target: 'prescricao-digital',
    gerar: api.prescricaoDigital.gerarPrescricaoDigital,
    emitir: api.prescricaoDigital.emitirPrescricaoDigital,
  },
  [TipoDocumentoAssinaturaDigitalPopup.ATESTADO]: {
    target: 'atestado-digital',
    gerar: api.atestado.gerarAtestadoDigital,
    emitir: api.atestado.emitirAtestadoDigital,
  },
  [TipoDocumentoAssinaturaDigitalPopup.BUNDLE_FHIR]: {
    target: 'bundle-fhir-digital',
    gerar: api.bundleFhirDigital.gerarBundleFhirDigital,
    emitir: api.bundleFhirDigital.emitirBundleFhirDigital,
  },
  [TipoDocumentoAssinaturaDigitalPopup.ATENDIMENTO]: {
    target: 'atendimento-digital',
    gerar: api.atendimentoDigital.gerarAtendimentoDigital,
    emitir: api.atendimentoDigital.emitirAtendimentoDigital,
  },
}

export enum DocumentoDigitalErrorCodeEnum {
  ASSINATURA_INVALIDA = 1,
  CONFIABILIDADE_INSUFICIENTE = 2,
  CPFS_DIFERENTES = 3,
  DOCUMENTO_JA_ASSINADO = 4,
  PROFISSIONAL_SEM_CONSELHO = 5,
  FALHA_ASSINAR_BUNDLE_FHIR = 6,
  BUNDLE_FHIR_INVALIDO = 7,
  API_VERIFHIR_INDISPONIVEL = 8,
}

export const documentoDigitalErrorCodeToMessage: Record<DocumentoDigitalErrorCodeEnum, React.ReactNode> = {
  [DocumentoDigitalErrorCodeEnum.ASSINATURA_INVALIDA]: 'Não foi possível obter a assinatura.',
  [DocumentoDigitalErrorCodeEnum.CONFIABILIDADE_INSUFICIENTE]: <ContaGovBrConfiabilidadeInsuficienteMessage />,
  [DocumentoDigitalErrorCodeEnum.CPFS_DIFERENTES]: 'O CPF do profissional não condiz com o CPF da assinatura.',
  [DocumentoDigitalErrorCodeEnum.DOCUMENTO_JA_ASSINADO]: 'O documento já foi assinado previamente.',
  [DocumentoDigitalErrorCodeEnum.PROFISSIONAL_SEM_CONSELHO]:
    'É necessário possuir um conselho de classe cadastrado para assinar um documento.',
  [DocumentoDigitalErrorCodeEnum.FALHA_ASSINAR_BUNDLE_FHIR]: 'Falha ao assinar bundle FHIR.',
  [DocumentoDigitalErrorCodeEnum.BUNDLE_FHIR_INVALIDO]: 'Bundle FHIR com formato inválido.',
  [DocumentoDigitalErrorCodeEnum.API_VERIFHIR_INDISPONIVEL]: 'API VeriFHIR indisponível.',
}

export const defaultAssinaturaDigitalErrorMessage = 'Não foi possível assinar o documento. Tente novamente mais tarde.'
export const assinaturaDigitalCancelMessage = 'Geração de documento digital cancelada.'
export const assinaturaDigitalSuccessMessage = 'Documento assinado com sucesso.'
